import { Icon, Portal } from '@chakra-ui/react';
import { useRef } from 'react';
import { MdClose } from 'react-icons/md';
import {
  MBox,
  MButton,
  MDivider,
  MDrawer,
  MDrawerBody,
  MDrawerCloseButton,
  MDrawerContent,
  MDrawerFooter,
  MDrawerHeader,
  MDrawerOverlay,
  MDrawerPositioner,
  MFlex,
  MStack,
  MTab,
  MTabList,
  MTabPanel,
  MTabPanels,
  MTabs,
} from '../../../../components/Monetize';
import { useACL } from '../../../../services/acl/acl';
import { OneTimeInvoiceContactsDrawerContacts } from './OneTimeInvoiceContactsDrawerContacts';

interface OneTimeInvoiceContactsDrawerProps {
  onClose: () => void;
}

/**
 * Contacts drawer for the one-time invoice form. Mirrors the quote contacts
 * drawer; invoices have no eSign recipients, so there is no eSign tab.
 */
export const OneTimeInvoiceContactsDrawer = ({
  onClose,
}: OneTimeInvoiceContactsDrawerProps) => {
  const cancelBtnRef = useRef(null);
  const { canDo } = useACL();

  return (
    <MDrawer
      open
      placement="end"
      onOpenChange={(e) => {
        if (!e.open) {
          onClose();
        }
      }}
      size="md"
      initialFocusEl={() => cancelBtnRef.current}
      onEscapeKeyDown={() => onClose()}
      onInteractOutside={() => onClose()}
      closeOnEscape={false}
      closeOnInteractOutside={false}
      restoreFocus={false}
      preventScroll={false}
    >
      <Portal>
        <MDrawerOverlay />
        <MDrawerPositioner>
          <MDrawerContent>
            <MDrawerCloseButton>
              <Icon asChild>
                <MdClose />
              </Icon>
            </MDrawerCloseButton>
            <MDrawerHeader>Contacts</MDrawerHeader>
            <MDrawerBody
              p={0}
              bg="tGray.sidebarDark"
              data-testid="contact-drawer-body"
            >
              <MTabs
                variant="line"
                size="sm"
                backgroundColor="tWhite.base"
                defaultIndex={0}
              >
                <MTabList px={6} pb={2}>
                  <MTab data-testid="contacts-contact-tab">Contacts</MTab>
                </MTabList>
                <MTabPanels p={2} bg="tGray.sidebarDark">
                  <MTabPanel p={0} data-testid="contacts-contact-content">
                    <MStack>
                      <OneTimeInvoiceContactsDrawerContacts
                        canUpdateContact={canDo([
                          ['account_contacts', 'update'],
                        ])}
                      />
                    </MStack>
                  </MTabPanel>
                </MTabPanels>
              </MTabs>
            </MDrawerBody>

            <MDivider />

            <MDrawerFooter>
              <MBox w="full">
                <MFlex justifyContent="center">
                  <MButton
                    ref={cancelBtnRef}
                    data-testid="contact-drawer-close-btn"
                    onClick={onClose}
                    variant="cancel"
                  >
                    Close
                  </MButton>
                </MFlex>
              </MBox>
            </MDrawerFooter>
          </MDrawerContent>
        </MDrawerPositioner>
      </Portal>
    </MDrawer>
  );
};
